import Typography from '@mui/material/Typography';
import Stack, { StackProps } from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { mdiCheck } from '@mdi/js';

import { Icon } from 'common/components/Icon';
import { Button } from 'common/components/Button';
import { CircularProgress } from 'common/components/CircularProgress';
import { useOpenCourse } from 'training/hooks/useOpenCourse';
import { CourseAITaskStatusBase } from 'training/components/CourseAITaskStatusBase';

interface IProps extends StackProps {
    taskId: string;
    onRetry?: () => void;
    onClose?: () => void;
}

enum EChapterState {
    queued = 'queued',
    inprogress = 'inprogress',
    done = 'done',
}

interface IAITranslateResultChapter {
    title: string;
    state: EChapterState;
}

interface IAITranslateResultData {
    progress: number;
    chapters: Record<string, IAITranslateResultChapter>;
    course_id: string | null;
}

const TRANSLATIONS = {
    title: 'AICourseTranslateTaskStatus.title',
    progressMessage: 'AICourseTranslateTaskStatus.translatingChapters',
    progressFinishingMessage:
        'AICourseTranslateTaskStatus.translatingCourseFinishing',
    failedMessage: 'AICourseTranslateTaskStatus.translatingCourseFailed',
    finishedMessage: 'AICourseTranslateTaskStatus.translatingCourseFinished',
    retryButton: 'AICourseTranslateTaskStatus.retry',
};

export const CourseAITranslateTaskStatus = ({
    taskId,
    onRetry,
    onClose,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { openCourseEdit, loadingCourseEdit } = useOpenCourse();

    const renderItems = (resultData: IAITranslateResultData | undefined) => {
        const chaptersObject = resultData?.chapters || {};
        const chapters = Object.entries(chaptersObject).map(
            ([id, chapter]) => ({
                id,
                ...chapter,
            })
        );

        return (
            <>
                {chapters.map((chapter) => {
                    const isChapterDone = chapter.state === EChapterState.done;
                    const isChapterInProgress =
                        chapter.state === EChapterState.inprogress;

                    return (
                        <Stack
                            alignItems="center"
                            direction="row"
                            key={chapter.id}
                            spacing={1}
                            sx={{ width: '100%', mb: 0.5 }}
                        >
                            {isChapterDone && (
                                <Icon
                                    path={mdiCheck}
                                    size="1.9rem"
                                    style={{
                                        color: 'var(--mui-palette-success-main)',
                                    }}
                                />
                            )}

                            {isChapterInProgress && (
                                <CircularProgress size={19} />
                            )}

                            <Typography sx={{ flex: 1 }} variant="body2">
                                {chapter.title}
                            </Typography>
                        </Stack>
                    );
                })}
            </>
        );
    };

    const shouldShowItems = (
        resultData: IAITranslateResultData | undefined
    ) => {
        const chaptersObject = resultData?.chapters || {};

        return Object.keys(chaptersObject).length > 0;
    };

    const isAllItemsComplete = (
        resultData: IAITranslateResultData | undefined
    ) => {
        const chaptersObject = resultData?.chapters || {};
        const chapters = Object.values(chaptersObject);
        const chapterCount = chapters.length;
        const completedChapters = chapters.filter(
            (ch) => ch.state === EChapterState.done
        ).length;

        return chapterCount > 0 && completedChapters === chapterCount;
    };

    const renderSuccessAction = (
        resultData: IAITranslateResultData | undefined
    ) => {
        const id = resultData?.course_id;

        if (!id) return null;

        return (
            <Button
                color="primary"
                loading={loadingCourseEdit}
                sx={{ width: '100%' }}
                variant="contained"
                onClick={() => openCourseEdit(id)}
            >
                {translate('viewCourse')}
            </Button>
        );
    };

    return (
        <CourseAITaskStatusBase<IAITranslateResultData>
            isAllItemsComplete={isAllItemsComplete}
            renderItems={renderItems}
            renderSuccessAction={renderSuccessAction}
            shouldShowItems={shouldShowItems}
            taskId={taskId}
            translations={TRANSLATIONS}
            onRetry={onRetry}
            {...other}
        />
    );
};
